import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Typography, Alert, IconButton, FormControlLabel, Checkbox,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dealersApi, type CreateAddressInput } from '../../../api/dealers.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

interface Props {
  dealerId: number | null;
  dealerName?: string;
  onClose: () => void;
}

const ADDRESS_TYPES = [
  { value: 'registered', label: 'Registered' },
  { value: 'billing', label: 'Billing' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'warehouse', label: 'Warehouse' },
];

const emptyForm: CreateAddressInput = { addressType: 'registered', line1: '' };

export function DealerAddressesDialog({ dealerId, dealerName, onClose }: Props) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = dealerId !== null;
  const [form, setForm] = useState<CreateAddressInput>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  const { data: addresses = [] } = useQuery({
    queryKey: ['dealer-addresses', dealerId],
    queryFn: () => dealersApi.listAddresses(dealerId as number),
    enabled: open,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['dealer-addresses', dealerId] });

  const addMut = useMutation({
    mutationFn: () => dealersApi.addAddress(dealerId as number, form),
    onSuccess: () => { setForm(emptyForm); setError(null); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const delMut = useMutation({
    mutationFn: (addressId: number) => dealersApi.removeAddress(dealerId as number, addressId),
    onSuccess: invalidate,
    onError: (e) => setError(extractApiError(e)),
  });

  const set = <K extends keyof CreateAddressInput>(k: K, v: CreateAddressInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Addresses — {dealerName}</DialogTitle>
      <DialogContent>
        {hasPermission('dealer_address.create') && (
          <>
            <Typography variant="subtitle2" gutterBottom>Add address</Typography>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            <Stack spacing={1.5} mb={2}>
              <Stack direction="row" spacing={2}>
                <TextField select size="small" label="Type" value={form.addressType} onChange={(e) => set('addressType', e.target.value)} sx={{ minWidth: 160 }}>
                  {ADDRESS_TYPES.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Label" value={form.label ?? ''} onChange={(e) => set('label', e.target.value)} />
                <FormControlLabel control={<Checkbox checked={form.isPrimary ?? false} onChange={(e) => set('isPrimary', e.target.checked)} />} label="Primary" />
              </Stack>
              <TextField size="small" label="Address line 1" value={form.line1} onChange={(e) => set('line1', e.target.value)} fullWidth />
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="City" value={form.city ?? ''} onChange={(e) => set('city', e.target.value)} fullWidth />
                <TextField size="small" label="Pincode" value={form.pincode ?? ''} onChange={(e) => set('pincode', e.target.value)} />
                <Button variant="contained" disabled={!form.line1} onClick={() => addMut.mutate()}>Add</Button>
              </Stack>
            </Stack>
          </>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell><TableCell>Address</TableCell><TableCell>City</TableCell><TableCell>Primary</TableCell><TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {addresses.map((a) => (
              <TableRow key={a.id}>
                <TableCell>{a.addressType}</TableCell>
                <TableCell>{a.line1}{a.line2 ? `, ${a.line2}` : ''}</TableCell>
                <TableCell>{a.city ?? '—'} {a.pincode ?? ''}</TableCell>
                <TableCell>{a.isPrimary ? <Chip size="small" color="primary" label="primary" /> : '—'}</TableCell>
                <TableCell align="right">
                  {hasPermission('dealer_address.delete') && (
                    <IconButton size="small" color="error" onClick={() => delMut.mutate(a.id)}><Delete fontSize="small" /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
